import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HREmployees.css';

const CITIES = ['All Cities', 'Lahore', 'Karachi', 'Islamabad', 'Multan'];
const ROLES  = ['employee', 'hr', 'admin'];

const SHIFTS = [
  { id: 1, name: 'Slot 1 — Standard (10:00–18:00)' },
  { id: 2, name: 'Slot 2 — Extended (10:00–19:00)' },
  { id: 3, name: 'Slot 3 — Evening (11:00–20:00)' },
];

const EMPTY_FORM = {
  employee_id: '', full_name: '', phone: '', email: '',
  department_id: '', job_title: '', location_id: '', shift_id: '1',
  base_salary: '', joining_date: '', tenure_years: '', role: 'employee',
  temp_password: '',
};

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['emp-av-blue','emp-av-teal','emp-av-amber','emp-av-purple','emp-av-coral','emp-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPKR(amount) {
  if (!amount && amount !== 0) return '—';
  return `PKR ${Number(amount).toLocaleString('en-PK')}`;
}

// ─── ADD / EDIT MODAL ────────────────────────────────────────────────────────

function EmployeeModal({ mode, employee, locations, departments, onSave, onClose }) {
  const [form, setForm] = useState(
    mode === 'edit' && employee
      ? {
          employee_id:   employee.employee_id   || '',
          full_name:     employee.full_name     || '',
          phone:         employee.phone         || '',
          email:         employee.email         || '',
          department_id: employee.department_id || '',
          job_title:     employee.job_title     || '',
          location_id:   employee.location_id   || '',
          shift_id:      employee.shift_id      || '1',
          base_salary:   employee.base_salary   || '',
          joining_date:  employee.joining_date ? employee.joining_date.split('T')[0] : '',
          tenure_years:  employee.tenure_years  || '',
          role:          employee.role          || 'employee',
          temp_password: '',
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.full_name.trim()) { setError('Full name is required'); return; }
    if (mode === 'add' && !form.employee_id.trim()) { setError('Employee ID is required'); return; }
    if (!form.location_id)  { setError('Location is required'); return; }

    setSaving(true);
    setError('');
    try {
      if (mode === 'add') {
        const res = await api.createEmployee({
          ...form,
          employee_id:   form.employee_id.toUpperCase(),
          base_salary:   form.base_salary   || 0,
          shift_id:      parseInt(form.shift_id),
          location_id:   parseInt(form.location_id),
          department_id: form.department_id ? parseInt(form.department_id) : null,
          tenure_years:  form.tenure_years  || 0,
        });
        if (res.error) { setError(res.error); return; }
      } else {
        const res = await api.updateEmployee(employee.id, {
          full_name:     form.full_name,
          phone:         form.phone,
          email:         form.email,
          department_id: form.department_id ? parseInt(form.department_id) : null,
          job_title:     form.job_title,
          location_id:   parseInt(form.location_id),
          shift_id:      parseInt(form.shift_id),
          base_salary:   form.base_salary || 0,
          role:          form.role,
        });
        if (res.error) { setError(res.error); return; }
      }
      onSave();
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="emp-modal-overlay" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <div className="emp-modal-header">
          <h2>{mode === 'add' ? 'Add New Employee' : 'Edit Employee'}</h2>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="emp-modal-error">{error}</div>}

        <div className="emp-modal-body">
          <div className="emp-form-section">Personal Info</div>
          <div className="emp-form-grid">
            {mode === 'add' && (
              <div className="emp-form-group">
                <label>Employee ID *</label>
                <input type="text" placeholder="e.g. EMP-010"
                  value={form.employee_id}
                  onChange={e => set('employee_id', e.target.value.toUpperCase())} />
              </div>
            )}
            <div className="emp-form-group">
              <label>Full Name *</label>
              <input type="text" placeholder="Full name"
                value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            </div>
            <div className="emp-form-group">
              <label>Phone</label>
              <input type="text" placeholder="e.g. 0300-1234567"
                value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="emp-form-group">
              <label>Email</label>
              <input type="email" placeholder="email@example.com"
                value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="emp-form-group">
              <label>Department</label>
              <select value={form.department_id} onChange={e => set('department_id', e.target.value)}>
                <option value="">Select department</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="emp-form-group">
              <label>Job Title</label>
              <input type="text" placeholder="e.g. Sales Executive"
                value={form.job_title} onChange={e => set('job_title', e.target.value)} />
            </div>
          </div>

          <div className="emp-form-section">Assignment</div>
          <div className="emp-form-grid">
            <div className="emp-form-group">
              <label>Location *</label>
              <select value={form.location_id} onChange={e => set('location_id', e.target.value)}>
                <option value="">Select location</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name} — {l.city}</option>
                ))}
              </select>
            </div>
            <div className="emp-form-group">
              <label>Shift</label>
              <select value={form.shift_id} onChange={e => set('shift_id', e.target.value)}>
                {SHIFTS.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="emp-form-group">
              <label>Role</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="emp-form-section">Salary & Dates</div>
          <div className="emp-form-grid">
            <div className="emp-form-group">
              <label>Base Salary (PKR)</label>
              <input type="number" placeholder="e.g. 35000"
                value={form.base_salary} onChange={e => set('base_salary', e.target.value)} />
            </div>
            {mode === 'add' && (
              <>
                <div className="emp-form-group">
                  <label>Joining Date</label>
                  <input type="date"
                    value={form.joining_date} onChange={e => set('joining_date', e.target.value)} />
                </div>
                <div className="emp-form-group">
                  <label>Tenure (years)</label>
                  <input type="number" step="0.1" placeholder="e.g. 2.5"
                    value={form.tenure_years} onChange={e => set('tenure_years', e.target.value)} />
                </div>
                <div className="emp-form-group">
                  <label>Temporary Password</label>
                  <input type="text" placeholder="Leave blank to set later"
                    value={form.temp_password} onChange={e => set('temp_password', e.target.value)} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="emp-modal-footer">
          <button className="emp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="emp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'add' ? 'Add Employee' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DEACTIVATE CONFIRM ───────────────────────────────────────────────────────

function DeactivateModal({ employee, onConfirm, onClose }) {
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };
  return (
    <div className="emp-modal-overlay" onClick={onClose}>
      <div className="emp-modal emp-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="emp-modal-header">
          <h2>Deactivate Employee</h2>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="emp-modal-body">
          <p className="emp-deactivate-msg">
            Are you sure you want to deactivate <strong>{employee?.full_name}</strong>?
            They will no longer be able to log in or clock in.
          </p>
        </div>
        <div className="emp-modal-footer">
          <button className="emp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="emp-btn-danger" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Deactivating…' : 'Yes, Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────

function EmployeeDetailPanel({ employeeId, onClose, onEdit, onDeactivate }) {
  const [employee, setEmployee] = useState(null);
  const [salary, setSalary]     = useState(null);
  const [loading, setLoading]   = useState(true);

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    api.getEmployee(employeeId).then(async emp => {
      setEmployee(emp);
      if (emp?.employee_id) {
        const sal = await api.calculateSalary(emp.employee_id, month, year);
        setSalary(sal);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [employeeId]);

  if (!employeeId) return null;

  return (
    <div className="emp-detail-panel">
      <div className="emp-detail-header">
        <button className="emp-detail-close" onClick={onClose}>✕</button>
        <h3>Employee Profile</h3>
        {employee && (
          <div className="emp-detail-actions">
            <button className="emp-detail-edit-btn" onClick={() => onEdit(employee)}>Edit</button>
            {employee.is_active && (
              <button className="emp-detail-deact-btn" onClick={() => onDeactivate(employee)}>
                Deactivate
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="emp-detail-loading"><div className="lb-spinner" /></div>
      ) : !employee ? (
        <div className="emp-detail-loading">Failed to load employee.</div>
      ) : (
        <div className="emp-detail-body">
          <div className="emp-detail-hero">
            <div className={`emp-detail-avatar ${getAvatarClass(employee.full_name)}`}>
              {getInitials(employee.full_name)}
            </div>
            <div className="emp-detail-hero-info">
              <span className="emp-detail-name">{employee.full_name}</span>
              <span className="emp-detail-code">{employee.employee_id}</span>
              <span className={`emp-detail-status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                {employee.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="emp-detail-section">Employee Details</div>
          {[
            { label: 'Phone',      val: employee.phone      || '—' },
            { label: 'Email',      val: employee.email      || '—' },
            { label: 'Department', val: employee.department_name || employee.department || '—' },
            { label: 'Job Title',  val: employee.job_title  || '—' },
            { label: 'Role',       val: employee.role       || '—' },
            { label: 'Location',   val: employee.location_name || '—' },
            { label: 'City',       val: employee.city       || '—' },
            { label: 'Shift',      val: employee.shift_name || '—' },
            { label: 'Joining Date', val: formatDate(employee.joining_date) },
            { label: 'Tenure',     val: employee.tenure_years ? `${employee.tenure_years} years` : '—' },
            { label: 'Base Salary', val: formatPKR(employee.base_salary) },
          ].map((r, i) => (
            <div key={i} className="emp-detail-row">
              <span className="emp-detail-lbl">{r.label}</span>
              <span className="emp-detail-val">{r.val}</span>
            </div>
          ))}

          <div className="emp-detail-section" style={{ marginTop: 20 }}>
            {now.toLocaleString('default', { month: 'long' })} {year} — Summary
          </div>
          {salary && !salary.error ? (
            [
              { label: 'Days Present',       val: salary.working_days_present ?? '—', color: 'green' },
              { label: 'Days Absent',        val: salary.absent_days          ?? '—', color: salary.absent_days > 0 ? 'red' : '' },
              { label: 'Late Count',         val: salary.late_count           ?? '—', color: salary.late_count > 0 ? 'amber' : '' },
              { label: 'Casual Leaves Used', val: salary.casual_leaves_consumed ?? '—' },
              { label: 'Approved Leaves',    val: salary.approved_leaves      ?? '—' },
              { label: 'Total Deduction',    val: formatPKR(salary.total_deduction), color: 'red' },
              { label: 'Net Salary',         val: formatPKR(salary.net_salary),      color: 'green' },
            ].map((r, i) => (
              <div key={i} className="emp-detail-row">
                <span className="emp-detail-lbl">{r.label}</span>
                <span className={`emp-detail-val ${r.color ? `emp-val-${r.color}` : ''}`}>{r.val}</span>
              </div>
            ))
          ) : (
            <div className="emp-detail-no-salary">No salary data for this month yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function HREmployees() {
  const [employees, setEmployees]       = useState([]);
  const [locations, setLocations]       = useState([]);
  const [departments, setDepartments]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [searchQuery, setSearchQuery]   = useState('');
  const [cityFilter, setCityFilter]     = useState('All Cities');
  const [deptFilter, setDeptFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedId, setSelectedId]     = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [deactivateEmployee, setDeactivateEmployee] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, locRes, deptRes] = await Promise.all([
        api.getAllEmployees(),
        api.getAllLocations(),
        api.getAllDepartments(),
      ]);
      setEmployees(empRes.employees || []);
      setLocations(locRes.locations || []);
      setDepartments(deptRes.departments || []);
      setError(null);
    } catch (err) {
      setError('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filtered list
  let filtered = [...employees];
  if (statusFilter === 'active')   filtered = filtered.filter(e => e.is_active);
  if (statusFilter === 'inactive') filtered = filtered.filter(e => !e.is_active);
  if (cityFilter !== 'All Cities') filtered = filtered.filter(e => e.city === cityFilter);
  if (deptFilter !== 'all')        filtered = filtered.filter(e => String(e.department_id) === deptFilter);
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(e =>
      (e.full_name     || '').toLowerCase().includes(q) ||
      (e.employee_id   || '').toLowerCase().includes(q) ||
      (e.department_name || e.department || '').toLowerCase().includes(q) ||
      (e.location_name || '').toLowerCase().includes(q)
    );
  }

  const handleDeactivateConfirm = async () => {
    await api.deactivateEmployee(deactivateEmployee.id);
    setDeactivateEmployee(null);
    setSelectedId(null);
    fetchData();
  };

  const totalActive   = employees.filter(e => e.is_active).length;
  const totalInactive = employees.filter(e => !e.is_active).length;

  return (
    <div className="emp-root">
      {/* Header */}
      <div className="emp-header">
        <div className="emp-header-left">
          <h1 className="emp-title">Employees</h1>
          <span className="emp-subtitle">{employees.length} total · {totalActive} active</span>
        </div>
        <button className="emp-add-btn" onClick={() => setShowAddModal(true)}>
          + Add Employee
        </button>
      </div>

      {/* Stats */}
      <div className="emp-stats">
        <div className="emp-stat-card">
          <span className="emp-stat-num">{employees.length}</span>
          <span className="emp-stat-lbl">Total</span>
        </div>
        <div className="emp-stat-card emp-stat-green">
          <span className="emp-stat-num">{totalActive}</span>
          <span className="emp-stat-lbl">Active</span>
        </div>
        <div className="emp-stat-card emp-stat-red">
          <span className="emp-stat-num">{totalInactive}</span>
          <span className="emp-stat-lbl">Inactive</span>
        </div>
        {departments.map(dept => (
          <div key={dept.id} className="emp-stat-card emp-stat-city">
            <span className="emp-stat-num">
              {employees.filter(e => e.department_id === dept.id && e.is_active).length}
            </span>
            <span className="emp-stat-lbl">{dept.name}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="emp-filters">
        <input
          className="emp-search"
          type="text"
          placeholder="Search by name, ID, department, location…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <div className="emp-filter-group">
          {CITIES.map(c => (
            <button key={c}
              className={`emp-filter-btn ${cityFilter === c ? 'emp-filter-active' : ''}`}
              onClick={() => setCityFilter(c)}
            >{c}</button>
          ))}
        </div>
        <div className="emp-filter-group">
          <button
            className={`emp-filter-btn ${deptFilter === 'all' ? 'emp-filter-active' : ''}`}
            onClick={() => setDeptFilter('all')}
          >All Depts</button>
          {departments.map(d => (
            <button key={d.id}
              className={`emp-filter-btn ${deptFilter === String(d.id) ? 'emp-filter-active' : ''}`}
              onClick={() => setDeptFilter(String(d.id))}
            >{d.name}</button>
          ))}
        </div>
        <div className="emp-filter-group">
          {[
            { key: 'active',   label: '● Active' },
            { key: 'inactive', label: '○ Inactive' },
            { key: 'all',      label: 'All' },
          ].map(s => (
            <button key={s.key}
              className={`emp-filter-btn ${statusFilter === s.key ? 'emp-filter-active' : ''}`}
              onClick={() => setStatusFilter(s.key)}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {/* Main area */}
      <div className={`emp-content ${selectedId ? 'emp-content-split' : ''}`}>
        <div className="emp-table-wrap">
          {error && <div className="emp-error">{error}</div>}
          {loading ? (
            <div className="emp-loading">
              <div className="lb-spinner" />
              <p>Loading employees…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="emp-empty">No employees match your filters.</div>
          ) : (
            <table className="emp-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Shift</th>
                  <th>Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => (
                  <tr key={emp.id}
                    className={`emp-row ${selectedId === emp.id ? 'emp-row-selected' : ''} ${!emp.is_active ? 'emp-row-inactive' : ''}`}
                    onClick={() => setSelectedId(selectedId === emp.id ? null : emp.id)}
                  >
                    <td>
                      <div className="lb-emp-cell">
                        <div className={`lb-avatar ${getAvatarClass(emp.full_name)}`}>
                          {getInitials(emp.full_name)}
                        </div>
                        <div className="lb-emp-info">
                          <span className="lb-emp-name">{emp.full_name}</span>
                          <span className="lb-emp-code">{emp.employee_id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="emp-dept-cell">
                        <span className="emp-dept">{emp.department_name || emp.department || '—'}</span>
                        <span className="emp-title-small">{emp.job_title || ''}</span>
                      </div>
                    </td>
                    <td>
                      <div className="lb-loc-cell">
                        <span className="lb-loc-name">{emp.location_name || '—'}</span>
                        <span className="lb-city-tag">{emp.city || ''}</span>
                      </div>
                    </td>
                    <td>
                      <span className="lb-shift-tag">{emp.shift_name || '—'}</span>
                    </td>
                    <td>
                      <span className="emp-salary">
                        {emp.base_salary ? formatPKR(emp.base_salary) : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`emp-status-badge ${emp.is_active ? 'emp-badge-active' : 'emp-badge-inactive'}`}>
                        {emp.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="emp-action-btns">
                        <button className="emp-action-edit" onClick={() => setEditEmployee(emp)}>Edit</button>
                        {emp.is_active && (
                          <button className="emp-action-deact" onClick={() => setDeactivateEmployee(emp)}>
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="emp-footer">
            Showing {filtered.length} of {employees.length} employees
          </div>
        </div>

        {selectedId && (
          <EmployeeDetailPanel
            employeeId={selectedId}
            onClose={() => setSelectedId(null)}
            onEdit={(emp) => setEditEmployee(emp)}
            onDeactivate={(emp) => setDeactivateEmployee(emp)}
          />
        )}
      </div>

      {showAddModal && (
        <EmployeeModal
          mode="add"
          locations={locations}
          departments={departments}
          onSave={() => { setShowAddModal(false); fetchData(); }}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {editEmployee && (
        <EmployeeModal
          mode="edit"
          employee={editEmployee}
          locations={locations}
          departments={departments}
          onSave={() => { setEditEmployee(null); fetchData(); }}
          onClose={() => setEditEmployee(null)}
        />
      )}

      {deactivateEmployee && (
        <DeactivateModal
          employee={deactivateEmployee}
          onConfirm={handleDeactivateConfirm}
          onClose={() => setDeactivateEmployee(null)}
        />
      )}
    </div>
  );
}